import {
  useCameraDetails,
  useTenantDetails,
} from '@hakimo-ui/hakimo/data-access';
import { LiveViewSwitch } from '@hakimo-ui/hakimo/feature-shared';
import { UnifiedAlarm } from '@hakimo-ui/hakimo/types';
import { BasicPanel, QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import { useMemo, useState } from 'react';
import AlarmMediaActions from '../alarm-details/alarm-media/actions/AlarmMediaActions';
import AlarmFrames from '../alarm-details/alarm-media/alarm-frames/AlarmFrames';
import { ScanDownloadButton } from '../alarm-details/alarm-media/actions/DownloadMediaButton';

interface Props {
  sharedToken?: string;
  event: UnifiedAlarm;
  onTalkdown: (cameraId: string, alarmId?: string) => void;
}

export function ScanRawAlarmMedia({ event, onTalkdown, sharedToken }: Props) {
  const [live, setLive] = useState(false);

  const { data: tenantData } = useTenantDetails(
    event.tenantId ?? event.location?.tenant_id ?? ''
  );
  const disableMediaDownload =
    Boolean(sharedToken) ||
    (tenantData ? tenantData.config?.disableMediaDownload ?? false : true);

  const cameraQueryResult = useCameraDetails(event.cameraId, undefined, live);
  // One answer to "does this event have playable frames", used by both the
  // download button and the player below.
  const frames = useMemo(
    () =>
      event.media.type === 'frames'
        ? (event.media.frames ?? []).map((frame) => ({
            ...frame,
            cameraName: event.cameraName,
            alarmType: event.alarm_type,
          }))
        : [],
    [event]
  );
  const hasFrames = frames.length > 0;

  const actions = (
    <AlarmMediaActions
      product="scan"
      cameraId={event.cameraId}
      alarmId={event.id}
      live={live}
      onTalkdown={onTalkdown}
      onToggleLive={setLive}
      isTalkdownEnabled={event.isTalkDownEnabled}
      sharedToken={sharedToken}
      downloadAction={
        // v1 is authed views only. Stays mounted and merely disabled without
        // frames, mirroring RgDownloadButton - unmounting would drop an
        // in-flight download's loading state.
        sharedToken || disableMediaDownload ? null : (
          <ScanDownloadButton alarmId={event.id} disabled={!hasFrames} />
        )
      }
    />
  );

  const renderContent =
    event.cameraId && live && !sharedToken ? (
      <div className="h-full w-full">
        <QueryResult queryResult={cameraQueryResult}>
          {(data) => (
            <LiveViewSwitch
              cameraId={data.id}
              livestreamUrl={data.livestreamUrl}
            />
          )}
        </QueryResult>
      </div>
    ) : hasFrames ? (
      <AlarmFrames
        frames={frames}
        timeZone={event.location?.timezone ?? 'UTC'}
        showMatchedFaces
        disableDownload={disableMediaDownload}
        showTimeline
      />
    ) : (
      <div className="flex h-64 w-full items-center justify-center text-sm text-gray-500 dark:text-gray-300">
        No event frames available
      </div>
    );

  return (
    <BasicPanel title="Event Media" secondary={actions}>
      {renderContent}
    </BasicPanel>
  );
}

export default ScanRawAlarmMedia;
